import { forwardRef, useRef, useImperativeHandle, CSSProperties } from "react";
import { cloneDeep, merge, map, forEach } from "lodash-es";
import { SlotMachineProps } from "./type";
import { transformFieldsToPx } from "@/utils/tools";
import styles from "./style.module.less";
import defaultBg from "./img/bg.png";
import VerticalSeamlessScroller from "./VerticalSeamlessScroller";

export type SlotMachineHandle = {
  play: () => void;
  stop: (targetIndices: number[]) => void;
};

const DEFAULT_CONFIG = {
  defaultConfig: {
    speed: 20,
    colSpacing: 0,
  },
};

const SlotMachine = forwardRef<SlotMachineHandle, SlotMachineProps>(
  (props, ref) => {
    const { blocks, topBar, bottomBar, prizes = [], ...rest } = props;
    const swiperRefs = useRef<(HTMLDivElement | null)[]>([]);

    // 背景处理
    const bgImg = blocks?.[0]?.imgs?.[0]?.src || defaultBg;
    const _blocks = cloneDeep(blocks);
    delete _blocks?.[0]?.imgs;

    // 配置转换
    const config = transformFieldsToPx(
      merge(DEFAULT_CONFIG, {
        width: "442rem",
        height: "140rem",
        blocks: _blocks,
        prizes,
        ...rest,
      }),
    );

    const onPlay = () => {
      forEach(swiperRefs.current, (v) => {
        v?.start();
      });
    };

    const onStop = (targets: number[]) => {
      forEach(swiperRefs.current, (v, index) => {
        v?.stop(targets[index]);
      });
    };

    useImperativeHandle(ref, () => ({
      play: onPlay,
      stop: onStop,
    }));

    return (
      <div
        className={styles["xply-slotMachine"]}
        style={{
          backgroundImage: `url(${bgImg})` as CSSProperties["backgroundImage"],
        }}
      >
        <div
          className={`${styles["xply-slotMachine-topBar"]} ${topBar?.className || ""}`}
          style={topBar?.style}
        >
          {topBar?.content}
        </div>

        <div className={styles["xply-slotMachine-wrapper"]}>
          <div
            className={styles["slot"]}
            style={{ width: config.width, height: config.height }}
          >
            {map(config.slots, (slot, index) => (
              <div key={index} className={styles["slot-item"]}>
                <VerticalSeamlessScroller
                  ref={(el) => (swiperRefs.current[index] = el)}
                  width={"100%"}
                  items={map(prizes, (prize) => {
                    return {
                      render: () => {
                        const img = prize?.imgs?.[0] || {};
                        return (
                          <div
                            style={{
                              width: "100%",
                              height: config.height,
                            }}
                            className={styles["prize"]}
                          >
                            <img
                              src={img?.src}
                              style={{
                                width: img.width,
                                height: img.height,
                              }}
                              alt=""
                            />
                          </div>
                        );
                      },
                    };
                  })}
                  scrollSpeed={slot?.speed * 15 || 2000}
                  transitionSpeed={2000}
                  initialSlide={slot?.order?.[0]}
                  directionUp={String(slot?.direction) !== "-1"}
                  onEnd={config?.onEnd}
                />
              </div>
            ))}
          </div>
        </div>

        <div
          className={`${styles["xply-slotMachine-bottomBar"]} ${bottomBar?.className || ""}`}
          style={bottomBar?.style}
        >
          {bottomBar?.content}
        </div>
      </div>
    );
  },
);

export default SlotMachine;
